import { useCallback, useEffect, useState } from "react";

import { Panel } from "reactflow";

import { Input } from "@components/Input";
import styles from "@styles/containers/oraculo/oracle-drawer.module.scss";

import { Categories } from "./variables/Categories";

import * as uuid from "uuid";

import { Titles } from "./variables/Titles";

import { ForEach, If } from "@components/control-statements";
import classNames from "classnames/bind";
import { VariableTypes } from "./variables/VariableTypes";
const css = classNames.bind(styles);

export const Sidebar = ({
  sort_and_limit,
  setNodes,
  nodes,
  setEdges,
  disabledVariables = [],
  onOpenOrdering,
}) => {
  const onDragStart = (event, nodeType) => {
    event.dataTransfer.setData("application/reactflow", nodeType);
    event.dataTransfer.effectAllowed = "move";
  };

  const [collapsed, setCollapsed] = useState([]);
  const [hideSidebar, setHideSidebar] = useState(false);
  const [search, setSearch] = useState("");
  const [filteredCategories, setFilteredCategories] = useState(Categories);

  // O produto de um pedido está sendo considerado por sua relação direta com o pedido
  const has_orders = useHasBoxType(nodes, ["o", "k", "w"]);
  const has_coupons = useHasBoxType(nodes, ["cc"]);
  const has_products = useHasBoxType(nodes, ["p", "op"]);

  const handleCollapse = (collapse) => {
    const collapses = [...collapsed];
    const index = collapses.indexOf(collapse);

    if (index !== -1) collapses.splice(index, 1);
    else collapses.push(collapse);

    setCollapsed(collapses);
  };

  const isAllowed = (category_alias) => {
    switch (category_alias) {
      case "orders":
      case "products":
        return !has_coupons;
      case "coupons":
        return !has_orders && !has_products;
    }
    return true;
  };

  const onAdd = useCallback(
    (event, nodeType) => {
      const lastNode = nodes[nodes.length - 1];

      let position;

      if (!lastNode) {
        position = {
          x: 315,
          y: 35,
        };
      } else {
        position = { x: lastNode.position.x + 280, y: lastNode.position.y };
      }

      const newNode = {
        id: uuid.v4(),
        type: nodeType,
        position,
        data: {
          type: VariableTypes[nodeType],
          label: `${Titles[nodeType]}`,
          variable: VariableTypes[nodeType],
          items: [],
        },
      };

      if (lastNode) {
        const newEdge = {
          id: `edge-${lastNode.id}-${newNode.id}`,
          source: lastNode.id,
          target: newNode.id,
          style: {
            strokeWidth: 0.5,
            stroke: "#4C4C4C",
          },
        };

        setEdges((prevEdges) => [...prevEdges, newEdge]);
      }

      setNodes(nodes.concat(newNode));
    },
    [nodes]
  );

  const onSearch = (text) => {
    setSearch(text);

    const search = Categories.map((c) => ({
      ...c,
      variables: c.variables.filter(
        (v) => v.label.toLowerCase().includes(text) || v.label.includes(text)
      ),
    })).filter((c) => c.variables.length > 0);

    for (let i = 0; i < search.length; i++) {
      const label = search[i].label;
      setCollapsed((prev) => [...prev, label]);
    }

    if (text?.length === 0) {
      setFilteredCategories(Categories);
      setCollapsed([]);
    } else {
      setFilteredCategories(search);
    }
  };

  return (
    <Panel
      position="left"
      style={{
        position: "absolute",
        top: 0,
        bottom: 0,
        margin: 0,
      }}
    >
      <aside className={css({ oracleSidebar: true, hide: hideSidebar })}>
        <Input
          search
          placeholder="Buscar por variáveis"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          className="mb-10 pt-0"
        />
        <div className={styles.categories}>
          {filteredCategories
            .filter((c) => c.visible === undefined || c.visible === true)
            .map((category) => (
              <Category
                category={category}
                hide={hideSidebar}
                disabled={!isAllowed(category.alias)}
                collapsed={collapsed.includes(category.label)}
                onCollapse={handleCollapse}
                onAdd={onAdd}
                disabledVariables={disabledVariables}
                onHideSideBar={(h) => setHideSidebar(h)}
              />
            ))}
        </div>
        <div className={styles.orderingAndRestriction} onClick={onOpenOrdering}>
          <div className="d-flex flex-column">
            <p>Ordenação e Limitação</p>
            <span className={styles.filter}>
              {sort_and_limit?.sort} / {sort_and_limit?.limit}
            </span>
          </div>
          <div className={styles.chevron}>
            <i className="fas fa-chevron-right" />
          </div>
        </div>
      </aside>
    </Panel>
  );
};

const Category = ({
  category,
  disabled = false,
  hide,
  collapsed,
  onCollapse,
  onAdd,
  onHideSideBar,
  disabledVariables = []
}) => {
  const label_lower = category.label.toLowerCase();
  const container_class = css({ collapse: true, [label_lower]: true });
  const container_style = disabled ? { opacity: 0.3 } : {};

  return (
    <div
      key={category.label}
      className={container_class}
      style={container_style}
    >
      <div
        className={styles.header}
        onClick={() => !disabled && onCollapse(category.label)}
      >
        <div className="d-flex align-center">
          <div className={styles.icon}>
            <i className={category.icon} />
          </div>
          <p className={styles.name}>{category.label}</p>
        </div>
        <div
          className={css({
            chevron: true,
            rotate: collapsed,
          })}
        >
          <i className="fas fa-chevron-down" />
        </div>
      </div>
      <If cond={!disabled}>
        <If cond={collapsed}>
          <div className={styles.collapsed}>
            <ul>
              <ForEach
                items={category.variables}
                render={(variable) => {
                  const is_enabled = !(disabledVariables || []).includes(variable.type) && VariableTypes[variable.type];
                  return (
                    <li
                      key={variable.label}
                      onClick={(event) => is_enabled ? onAdd(event, variable.type) : null}
                      style={{
                        opacity: is_enabled ? 1 : 0.3,
                      }}
                    >
                      <span>{variable.label}</span>
                      <i className="far fa-plus" />
                    </li>
                  );
                }}
              />
            </ul>
          </div>
        </If>
      </If>
      <div
        className={css({ hide: true, rotate: hide })}
        onClick={() => onHideSideBar(!hide)}
      >
        <i className="fas fa-chevron-left" />
      </div>
    </div>
  );
};

const useHasBoxType = (nodes, types = []) => {
  const [has, setHas] = useState(false);

  const handler = () => {
    const node_types = nodes.map((n) => n.data.type);
    const nodeHasType = (node_type) => {
      return types.some((t) => {
        return node_type.startsWith(`${t}.`);
      });
    };

    setHas(node_types.some((n) => nodeHasType(n)));
  };

  useEffect(handler);
  return has;
};

export default Sidebar;
